import { IChecklistItem } from 'pages/Admin'
import { workflowRestrictions } from 'pages/Admin/AdminTools/Configuration/Workflows/constants'
import { assetInfoValidate } from 'pages/LoanApplication/AssetInformation/logics'
import { BorrowerSeperator } from 'pages/LoanApplication/config'
import {
  closingCostEstimateIsSameFeeWorksheet,
  isSameStructureApplicationCreditScore,
} from 'pages/LoanApplication/CreditScore/logic'
import { validateLoanDetailCompletion } from 'pages/LoanStructure/Logic'
import { isBorrowerTermSheetSatisfied } from 'pages/LoanStructure/termSheetSignLogic'
import { store } from 'reducers'
import { LoanType } from 'reducers/loanDetail.reducer'
import { isIneligiblePartyCleared } from 'stories/layouts/FloaterTabs/logic'
import { removeComma } from 'utils'

export const checkCheckListVisibility = (
  item: IChecklistItem,
  data: Record<string, any>,
  reduxState?: {
    loan?: Record<string, any>
    borrower?: Record<string, any>
  },
) => {
  if (!item.title || !item.inputType) return false
  const orLogics = item.visibleLogic
  let hasNoLogic = !orLogics || !orLogics.length
  let logicCounts = 0
  let canBeShown = false

  orLogics?.map((orLogic: any) => {
    if (!orLogic || !orLogic.length) return
    let flag = true
    orLogic.map((andLogic: any) => {
      const { fieldOrigin, fieldKey, value } = andLogic

      let _value

      if (!fieldOrigin || fieldOrigin === 'ChecklistFields') {
        _value = data[fieldKey]
      } else {
        let originObj
        switch (fieldOrigin) {
          case 'LoanStructure':
          case 'PropertyInformation':
            originObj = reduxState?.loan
            break
          case 'BorrowerInformation':
          case 'DeclarationsHMDA':
            originObj = reduxState?.borrower?.borrower
            break
          case 'AssetLiability':
          case 'TrackRecord':
            originObj = reduxState?.borrower?.borrower
            break
          default:
            originObj = undefined
        }

        if (originObj) {
          _value = Object.keys(originObj).includes(fieldKey)
            ? originObj[fieldKey]
            : originObj.customInformation?.[fieldKey]
        }
      }

      flag = flag && _value == value
    })
    canBeShown = canBeShown || flag
    logicCounts += orLogic.length
  })

  hasNoLogic = hasNoLogic || !logicCounts

  return canBeShown || hasNoLogic
}

export const getLoanRestrictionErrors = (
  restrictions: string[] | null = null,
  options: { scopeOfWork?: boolean; currentTemplate?: number } = {},
) => {
  const errors: string[] = []
  const { loan, loanDetail, step } = store.getState()
  const appDetail = step.overview.application.detail
  const { loanType, closeParties, parties } = loanDetail
  const assetInfoValidation = assetInfoValidate(BorrowerSeperator.BORROWER, false)
  const { scopeOfWork = loanDetail.scopeOfWorkDone, currentTemplate = loanDetail.templateNumber } = options

  if (!restrictions) restrictions = Object.keys(workflowRestrictions)

  // Loan Structure
  if (restrictions.includes('loanStructureDataComplete')) {
    const isValidate = validateLoanDetailCompletion(loan)
    if (!isValidate) errors.push(workflowRestrictions.loanStructureDataComplete)
  }

  if (restrictions.includes('loanStructurePrice') && loanDetail.rateData.rate <= 0) {
    errors.push(workflowRestrictions.loanStructurePrice)
  }

  // submit_loan
  if (restrictions.includes('accountExecutiveRequired') && !parties?.accountExecutive?.email?.length)
    errors.push(workflowRestrictions.accountExecutiveRequired)

  if (restrictions.includes('accountManagerRequired') && !parties?.accountManager?.email?.length)
    errors.push(workflowRestrictions.accountManagerRequired)

  if (restrictions.includes('dataComplete') && appDetail.dataComplete === -1)
    errors.push(workflowRestrictions.dataComplete)

  if (restrictions.includes('signApplication') && appDetail.signApplication === -1)
    errors.push(workflowRestrictions.signApplication)

  if (restrictions.includes('assetLiability') && assetInfoValidation === false)
    errors.push(workflowRestrictions.assetLiability)

  if (loanType !== LoanType.CORRESPONDENT) {
    if (restrictions?.includes('creditScorePull') && appDetail.creditScorePull === -1)
      errors.push(workflowRestrictions.creditScorePull)
    else {
      const scoreMatch = isSameStructureApplicationCreditScore()
      if (restrictions.includes('scoreMatch') && !scoreMatch.same) errors.push(`Loan Structure -> ${scoreMatch.error}`)
    }
  }

  const sameEstimate = closingCostEstimateIsSameFeeWorksheet()
  if (restrictions.includes('estimateMatch') && !sameEstimate.same)
    errors.push(`Loan Structure -> ${sameEstimate.error}`)

  if (restrictions.includes('loanOfficerRquired') && !parties.loanOfficer?.name?.length)
    errors.push(workflowRestrictions.loanOfficerRquired)

  if (restrictions.includes('loanProcessorRequired') && !parties.loanProcessor?.name?.length)
    errors.push(workflowRestrictions.loanProcessorRequired)

  if (restrictions.includes('titleCompanyRequired') && !closeParties.TitleCompany?.FirstName?.length)
    errors.push(workflowRestrictions.titleCompanyRequired)

  if (restrictions.includes('hazardCompanyRequired') && !closeParties.HazardInsuranceCompany?.FirstName?.length)
    errors.push(workflowRestrictions.hazardCompanyRequired)

  if (restrictions.includes('scopeOfWork') && !scopeOfWork) errors.push(workflowRestrictions.scopeOfWork)

  // submit_to_loan_setup
  if (restrictions.includes('submitToLoanSetupRequired') && loanDetail.submitToLoanSetup === 0)
    errors.push(workflowRestrictions.submitToLoanSetupRequired)

  // submit_to_underwriter
  if (restrictions.includes('conditionTemplateRequired') && currentTemplate === 0)
    errors.push(workflowRestrictions.conditionTemplateRequired)

  if (restrictions?.includes('underwriterRequired') && !parties?.underwriter?.email?.length)
    errors.push(workflowRestrictions.underwriterRequired)

  if (restrictions?.includes('estimatedClosingDateRequired') && !loanDetail.closingDate)
    errors.push(workflowRestrictions.estimatedClosingDateRequired)

  if (restrictions?.includes('ineligibleFlagsCleared') && !isIneligiblePartyCleared())
    errors.push(workflowRestrictions.ineligibleFlagsCleared)

  if (restrictions?.includes('ineligiblePartiesCleared') && !isIneligiblePartyCleared())
    errors.push(workflowRestrictions.ineligiblePartiesCleared)

  if (restrictions?.includes('loanMustbeLocked') && !loanDetail.rateData.locked)
    errors.push(workflowRestrictions.loanMustbeLocked)

  if (
    restrictions?.includes('borrowerHasSignedTermSheet') &&
    !isBorrowerTermSheetSatisfied({
      termSheetOverride: loanDetail.termSheetOverride,
      termSheetWetSigned: loanDetail.termSheetWetSigned,
      termSheetSignedPdfKey: loanDetail.termSheetSignedPdfKey,
      termSheetSignedDate: loanDetail.termSheetSignedDate,
    })
  )
    errors.push(workflowRestrictions.borrowerHasSignedTermSheet)

  if (
    restrictions?.includes('asIsValueLessThanAsIsAppraisedValue') &&
    removeComma(loan.asIsValue) > removeComma(loanDetail.reviewForm?.appraiserAsIsProperty || 0)
  )
    errors.push(workflowRestrictions.asIsValueLessThanAsIsAppraisedValue)

  if (
    restrictions?.includes('afterRepairValueLessThanAfterRepairAppraisedValue') &&
    removeComma(loan.afterRepairValue) > removeComma(loanDetail.reviewForm?.appraiserAfterRepairProperty || 0)
  )
    errors.push(workflowRestrictions.afterRepairValueLessThanAfterRepairAppraisedValue)

  return errors
}
